import {
  Box,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Split from "react-split";
import { encodingConstant } from "../../../constants/constantList";
import CommonGrid from "../../Repository/CommonGrid";
import TabPanel from "../../TabPanel";
import { Loader } from "../../../utils/loader";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "../../../pages/workbench_build/WorkbenchBuild";
import { t } from "i18next";
import { treeActions } from "../../../redux/slices/treeSlice";
import { api } from "../../../redux/api";
import { cloneDeep, isArray } from "lodash";
import { getUpdatedData } from "../../RightContextMenu/ContextActions/NewRecordOutbound";
import { eachDeep } from "deepdash-es/standalone";
import { genKey } from "../../../utils/utilFunctions";
import LightTooltip from "../../../commonComponents/LightTooltip";
const columnDefNameMaping = {
  name: t("COL_NAME"),
  dataType: t("COL_TYPE"),
  typeName: t("COL_TYPE_NAME"),
  columnSize: t("COL_LENGTH"),
  remarks: t("COL_REMARKS"),
  isNullable: t("COL_NULL"),
  decimalDigits: t("COL_DECIMAL_DIGITS"),
};

function TableViewer({
  openTableViewer,
  handleClose,
  schema,
  tableName,
  connection,
  setShowAlert
}) {
  const { t } = useTranslation();
  const [encoding, setEncoding] = useState("DEFAULT_ENCODING");
  const [currTab, setCurrTab] = useState(0);
  const [rowData, setRowData] = useState({
    metaData: [],
    top25: [],
  });
  const [gridOptions, setGridOptions] = useState({
    metaData: {},
    top25: {},
  });
  const [selectedMetaData, setSelectedMetaData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const dispatch = useDispatch();
  const queryParams = useSelector((state) => state.queryParams);
  const parentScreen = useContext(ParentScreenCtx);
  const rightTree = useSelector(
    (state) => state?.treeData?.[parentScreen]?.rightTree
  );
  const selectedNode = rightTree?.tree?.[rightTree?.currentTab]?.selectedNode;
  const translatedData = rightTree.tree[rightTree?.currentTab].translatedData;
  const metaLoading = useSelector(
    (state) =>
      state?.treeData?.[parentScreen]?.loadingState?.["META_LOADING"]
        ?.isLoading || false
  );
  const top25Loading = useSelector(
    (state) =>
      state?.treeData?.[parentScreen]?.loadingState?.["TOP25_LOADING"]
        ?.isLoading || false
  );
  const metaError = useSelector(
    (state) =>
      state?.treeData?.[parentScreen]?.loadingState?.["META_LOADING"]
        ?.isError || false
  );
  const top25Error = useSelector(
    (state) =>
      state?.treeData?.[parentScreen]?.loadingState?.["TOP25_LOADING"]
        ?.isError || false
  );
  const metaTimer = useRef();
  const top25Timer = useRef();

  const getGridAPI = (api) => {
    setGridApi(api);
  };

  const nothingToDisplay = useMemo(() => {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          fontSize: "16px",
          color: "#b5b2ba",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
        {t("TRACKING_REPORTS_NOTHING_TO_DISPLAY")}
      </div>
    );
  });
  const onRowDataUpdated = (gridOptions) => {
    selectedMetaData?.forEach((e) => {
      gridOptions.api.forEachNode((node) => {
        if (node?.data?.name === e?.name) {
          node.setSelected(true);
        }
      });
    });
  };
  // This Function is keeping only those columns in AG-grid which has atleast one value to show in that coloumn;
  const updateNewGridOptionsMeta = (data, type) => {
    const columnDefs = [];
    Object.keys(columnDefNameMaping).forEach((e) => {
      for (let i = 0; i < data.length; i++) {
        const ele = data[i];
        if (ele[e]) {
          columnDefs.push({
            headerName: columnDefNameMaping[e],
            field: e,
            resizable: true,
            editable: false,
            showPlaceholder: false,
          });
          break;
        }
      }
    });
    setGridOptions((prev) => {
      return { ...prev, metaData: { columnDefs } };
    });
  };
  const updateNewGridOptionsGeneric = (data = [], rowData = [], type) => {
    const columnDefs = [];
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < rowData.length; j++) {
        if (rowData?.[j]?.[i]) {
          columnDefs.push({
            headerName: data[i],
            field: data[i],
            resizable: true,
            editable: false,
            showPlaceholder: false,
          });
          break;
        }
      }
    }
    setGridOptions((prev) => {
      return { ...prev, [type]: { columnDefs } };
    });
  };
  const updateMetaDataHandler = (sort = true) => {
    if (!tableName || !schema) return;
    if (metaTimer.current) clearTimeout(metaTimer.current);
    dispatch(
      treeActions.setLoadingActions({
        screen: parentScreen,
        key: "META_LOADING",
        data: { isLoading: true, isError: false },
      })
    );
    dispatch(
      api.endpoints.get_metadata.initiate(
        {
          account: queryParams.account,
          direction: parentScreen === "inbound" ? "IN" : "OUT",
          table_name: tableName,
          pdc_name: connection,
          sort: sort,
          schema: schema,
        },
        { forceRefetch: true }
      )
    ).then((res) => {
      if (res?.data?.current_state === "SUCCESS" && isArray(res?.data?.data)) {
        let rowData = [...(res?.data?.data || [])];
        updateNewGridOptionsMeta(rowData);
        if (sort)
          rowData = rowData.sort((a, b) => a?.name?.localeCompare(b?.name));
        setRowData((prev) => {
          return { ...prev, metaData: rowData };
        });
      } else {
        const message =
          !isArray(res?.data?.data) && res?.data?.data
            ? res?.data?.data
            : res?.data?.message;
        setShowAlert({ message: message, isOpen: true, type: "warning" });
      }
    });
  };
  const updateTop25DataHandler = (sort = true) => {
    if (!tableName || !schema) return;
    if (top25Timer.current) clearTimeout(top25Timer.current);
    dispatch(
      treeActions.setLoadingActions({
        screen: parentScreen,
        key: "TOP25_LOADING",
        data: { isLoading: true, isError: false },
      })
    );
    dispatch(
      api.endpoints.get_top_25_records.initiate(
        {
          account: queryParams.account,
          direction: parentScreen === "inbound" ? "IN" : "OUT",
          table_name: tableName,
          pdc_name: connection,
          encoding: encoding,
          schema: schema,
        },
        { forceRefetch: true }
      )
    ).then((res) => {
      if (res?.data?.current_state === "SUCCESS" && isArray(res?.data?.data)) {
        const rowData = [...(res?.data?.data || [])];
        const headers = rowData.splice(0, 1)[0];
        if (rowData[0] && !rowData?.[0]?.[0]?.includes(t("LBL_NO_RECORDS"))) {
          updateNewGridOptionsGeneric(headers, rowData, "top25");
          const newRowData = rowData.map((e) => {
            const ele = {};
            for (let i = 0; i < e.length; i++) {
              ele[headers[i]] = e[i];
            }
            return ele;
          });
          setRowData((prev) => {
            return { ...prev, top25: newRowData };
          });
        }
      } else {
        const message =
          !isArray(res?.data?.data) && res?.data?.data
            ? res?.data?.data
            : res?.data?.message;
        setShowAlert({ message: message, isOpen: true, type: "warning" });
      }
    });
  };
  const encodingOptions = Object.keys(encodingConstant).map((e, i) => {
    return (
      <MenuItem value={e} key={i}>
        {t(encodingConstant[e])}
      </MenuItem>
    );
  });
  const handleEncodingChange = (e) => {
    const { value } = e.target;
    setEncoding(value);
  };
  const handleTabChange = (e, newValue) => {
    setCurrTab(newValue);
  };
  const metaGridSelectionHandler = (gridOptions) => {
    const selectedRows = gridOptions.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedMetaData(selectedRows);
    } else {
      setSelectedMetaData([]);
    }
  };
  const addFieldshandler = () => {
    dispatch(
      treeActions.setTreeActions({
        data: {
          treeType: rightTree.currentTab,
          expandKeys: [selectedNode?.key],
        },
      })
    );
    const selectedNodeKey = selectedNode?.key;
    const newNodes = [];

    // Making New Nodes to Add
    selectedMetaData?.forEach((e, i) => {
      newNodes.push({
        COLUMN_NAME: e.name,
        DESCRIPTION: e.name,
        FIELDTYPE: e.dataType,
        ICON_DISPLAY: "DATABASE_FIELD",
        MAPNAME: e.name,
        TABLE_NAME: tableName,
        title: `${t("LBL_FIELD")} ${e.name}`,
        key: selectedNodeKey + `-${i}`,
        nodeId: genKey(),
        ...(e.dataType === 'FLOAT' ? { ROUND:'TRUE',LENGTH : "0",PADDIR:'RIGHT',PRECISION:"0",UNIQUE:"FALSE", FILLCHAR: "+", KEY:"FALSE", QUOTE_DATA: "TRUE" } : {})
      });
    });

    const newTranslatedData = cloneDeep(translatedData);
    eachDeep(newTranslatedData, (obj) => {
      if (obj?.key && obj?.key === selectedNodeKey) {
        obj.children = [...newNodes, ...(obj?.children || [])];
        dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: rightTree?.currentTab,
            data: obj,
          })
        );
      }
    });

    dispatch(
      treeActions.setRightTreeTransData({
        screen: parentScreen,
        type: rightTree?.currentTab,
        data: getUpdatedData(newTranslatedData, "DATABASE", false, false, rightTree?.currentTab),
      })
    );
    handleClose();
  };
  useEffect(() => {
    if (schema && tableName && currTab === 0) {
      updateMetaDataHandler();
    } else if (currTab === 1) {
      updateTop25DataHandler();
    }
  }, [openTableViewer, currTab, encoding]);

  useEffect(() => {
    const message = t("BOX_MSG_GENERAL_ERROR");
    if (metaError && metaLoading) {
      setShowAlert({ message, isOpen: true, type: "warning" });
      dispatch(
        treeActions.setLoadingActions({
          screen: parentScreen,
          key: "META_LOADING",
          data: { isLoading: false, isError: false },
        })
      );
    }
    if (top25Error && top25Loading) {
      setShowAlert({ message, isOpen: true, type: "warning" });
      dispatch(
        treeActions.setLoadingActions({
          screen: parentScreen,
          key: "TOP25_LOADING",
          data: { isLoading: false, isError: false },
        })
      );
    }
  }, [metaError, top25Error]);

  return (
    <>
      <Drawer
        anchor="left"
        open={openTableViewer}
        onClose={handleClose}
        sx={{
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: "100%",
            boxSizing: "border-box",
            background: "transparent",
          },
        }}>
        <Split
          className="split height-100 flex"
          direction="horizontal"
          gutterSize={4}
          snapOffset={0}
          dragInterval={4}
          minSize={[550, 0]}
          sizes={[67, 33]}
          children={[
            <div
              className="flex-column height-100"
              key="main"
              style={{ backgroundColor: "#fff" }}>
              <div
                key={0}
                style={{
                  borderBottom: "1px solid #e0e0e0",
                  height: "60px",
                  boxSizing: "border-box",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontWeight: "500",
                  paddingLeft: "16px",
                }}>
                <div className="dialog-header">
                  <div
                    className="flex align-center title"
                    style={{ gap: "6px" }}>
                    <span className="TABLE_VIEW"></span>
                    <span>{t("DIAG_TABLE_VIEWER_TIT")}</span>
                  </div>
                  <div
                    className="description font-12 margin-top-4"
                    style={{ marginLeft: "32px" }}>
                    {t("DIAG_TABLE_VIEWER_DESC")}
                  </div>
                </div>
                <IconButton onClick={handleClose} sx={{ borderRadius: "5px" }}>
                  <CloseIcon
                    sx={{ height: "28px", width: "28px", color: "#777777" }}
                  />
                </IconButton>
              </div>
              <div className="flex-column margin-12">
                <div>
                  <FormControl
                    variant="outlined"
                    size="small"
                    className="width-100">
                    <InputLabel id="encoding">{t("LBL_ENCODING")}</InputLabel>
                    <Select
                      label={t("LBL_ENCODING")}
                      className="width-100"
                      value={encoding}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                          },
                        },
                      }}
                      onChange={handleEncodingChange}>
                      {encodingOptions}
                    </Select>
                  </FormControl>
                </div>
                <div className="height-100">
                  <Box
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      display: "flex",
                      justifyContent: "space-between",
                    }}>
                    <Tabs onChange={handleTabChange} value={currTab}>
                      <Tab
                        label={
                          <div className="flex align-center">
                            <span className="flex align-center">
                              <span className="BALLOT"></span>
                              <span>{t("DB_NAV_TAB_METADATA")}</span>
                            </span>
                          </div>
                        }
                      />
                      <Tab
                        label={
                          <div className="flex align-center">
                            <span className="flex align-center">
                              <span className="TABLE"></span>
                              <span>{t("DB_NAV_TAB_TOP25_RECS")}</span>
                            </span>
                          </div>
                        }
                      />
                    </Tabs>
                    {currTab === 0 ? (
                      <LightTooltip title={t("DB_NAV_ADD_SELECTED_FIELDS")}>
                        <span>
                          <Button
                            size="small"
                            onClick={addFieldshandler}
                            disabled={!selectedMetaData?.length}
                            style={{ margin: "5px" }}
                            variant="outlined">
                            {t("ADD")}
                          </Button>
                        </span>
                      </LightTooltip>
                    ) : (
                      ""
                    )}
                  </Box>
                  {currTab === 0 && (
                    <TabPanel
                      value={currTab}
                      className={"overflow-auto"}
                      index={0}>
                      <Box
                        sx={{ position: "relative" }}
                        className={"height-100 overflow-auto"}>
                        {metaLoading ? (
                          <Loader />
                        ) : Object.keys(gridOptions.metaData).length ? (
                          <CommonGrid
                            gridOptions={{
                              ...gridOptions.metaData,
                              onSelectionChanged: metaGridSelectionHandler,
                            }}
                            rowData={rowData.metaData}
                            getGridAPI={getGridAPI}
                            containerClasses="table-viewer-grid-container"
                            gridProps={{
                              rowSelection: "multiple",
                              onRowDataUpdated: onRowDataUpdated,
                            }}
                          />
                        ) : (
                          nothingToDisplay
                        )}
                      </Box>
                    </TabPanel>
                  )}
                  {currTab === 1 && (
                    <TabPanel
                      value={currTab}
                      index={1}
                      className="height-100 overflow-auto">
                      {top25Loading ? (
                        <Loader />
                      ) : Object.keys(gridOptions.top25).length ? (
                        <CommonGrid
                          fullSize={false}
                          gridOptions={gridOptions.top25}
                          rowData={rowData.top25}
                          containerClasses="table-viewer-grid-container"
                        />
                      ) : (
                        nothingToDisplay
                      )}
                    </TabPanel>
                  )}
                </div>
                {currTab === 0 &&
                  selectedMetaData.length > 0 &&
                  !metaLoading && (
                    <div style={{ margin: "5px" }}>
                      <p>{`${selectedMetaData.length} ${
                        selectedMetaData.length > 1 ? "rows" : "row"
                      } selected`}</p>
                    </div>
                  )}
              </div>
            </div>,
            <div
              key="supportingDiv"
              style={{ background: "transparent" }}></div>,
          ]}></Split>
      </Drawer>
    </>
  );
}

export default TableViewer;
